import DOMPurify from 'dompurify';
import BaseElement from "./BaseElement";
import TableBuilder from './index';
import {extractInlineFont, getFontFaceStyle, groupConnectedCells} from "../helpers";
import {
  ALLOWED_STYLES_TO_APPLY_TO_ALL, 
  APPLY_TO_BORDERS_VALUES,
  APPLY_TO_VALUES,
  BORDER_STYLES,
  BORDERS,
  CELL_BORDER_STYLE_KEYS,
  CELL_ANGLE_FITTED_RULE,
  CELL_ANGLE_FIT_HEIGHT_RULE,
  CELL_ANGLE_FIT_WIDTH_RULE,
  CELL_STYLE_KEYS,
  ROW_STYLE_KEYS,
} from "@frontend/group/modules/simple-table-builder/constants";

const DEFAUL_COLUMN_TITLE = 'Column';
const DEFAUL_CELL_VALUE = 'Value';

// Custom fonts only ship a regular weight, and the browser's faux-bold synthesis is
// unreliable when the SVG is rasterised as an image (show page). Render bold text with
// an explicit hairline stroke instead, and disable font-synthesis so both the image
// (show page) and live (PDF) rasterisers produce the same weight.
// Preview-only hairline boost: off at 1:1, and the cap keeps a thumbnail zoom from turning into
// a mesh (uncapped, a 9% zoom painted 7px bands over 4px rows).
const HAIRLINE_BOOST_MAX_SCALE = 0.95;
const HAIRLINE_BOOST_MAX_PX = 1.5;
// Device pixels of ink a boosted border should land on. Under 1 on purpose — the downscale
// spreads it over two or three pixels, so 1 already reads wider than a crisp line.
const HAIRLINE_TARGET_PX = 0.8;

const BOLD_SYNTHESIS_STYLE = `<style> b, strong { font-synthesis: none; -webkit-text-stroke: 0.05em currentColor; } </style>`;

/**
 * Repaint the cell borders that a downscaled raster would otherwise lose. Only edges between two
 * cells sharing a background need it — where the backgrounds differ the colour change carries the
 * edge on its own, and boosting those too is what made the grid look heavy.
 *
 * Bands are one-sided so the two cells either side of an edge don't stack them, and `box-shadow`
 * takes no part in layout, so the table measures and exports exactly as before.
 *
 * @param {string} html
 * @param {number} scale - screen pixels one source pixel will cover; at 1 (or near it) a no-op.
 * @param {{right: boolean, bottom: boolean}[]} edges - per cell, in the order toHTML emits them.
 */
function boostHairlines(html, scale, edges) {
  if (!(scale > 0) || scale >= HAIRLINE_BOOST_MAX_SCALE || !edges || !edges.length) return html;

  // Fractional: rounding to whole pixels overshoots the target badly on the upper zoom steps.
  const ring = Math.round(Math.min(HAIRLINE_BOOST_MAX_PX, HAIRLINE_TARGET_PX / scale - 1) * 100) / 100;

  if (!(ring > 0.02)) return html;

  let index = 0;

  return html.replace(/<t[dh]\b[^>]*>/g, (tag) => {
    const edge = edges[index++];

    if (!edge || (!edge.right && !edge.bottom)) return tag;

    const style = /\sstyle="([^"]*)"/.exec(tag);
    const color = style && /(?:^|;)\s*border-color:\s*([^;]+)/.exec(style[1]);
    const width = style && /(?:^|;)\s*border-width:\s*([\d.]+)px/.exec(style[1]);

    if (!color || (width && parseFloat(width[1]) === 0)) return tag;

    const bands = [];
    if (edge.right) bands.push(`inset -${ring}px 0 0 0 ${color[1].trim()}`);
    if (edge.bottom) bands.push(`inset 0 -${ring}px 0 0 ${color[1].trim()}`);

    return tag.replace(style[0], ` style="${style[1]}box-shadow: ${bands.join(', ')};"`);
  });
}

export default class Table extends BaseElement {

  headers = [];
  
  staticHeaders = [];

  staticFooters = [];

  /**
   * @type {Styles}
   */
  headerRowStyles = new TableBuilder.Styles();

  rows = [];

  autoFontSize = false;
  autoFontSizeValue = null;
  tableColumnMode = false;
  headerVisible = true;
  mergeSameValues = false;
  truncateLongText = false;
  columnModeEnabled = true;
  defaultCellPaddingShorthand = "2 5";
  borderBoxWidths = false;

  constructor() {
    super();

    this.styles.setRules({
      'border-collapse': 'collapse',
      'color': '#000000',
      'height': 'auto',
      'padding': '0'
    });
  }
  
  setDefaultCellPaddingShorthand(padding) {
    this.defaultCellPaddingShorthand = padding;
  }

  setHeaders(headers) {
    this.headers = headers;
  }

  setStaticHeaders(staticHeaders) {
    const bottomRows = this.staticFooters || [];
    this.staticHeaders = this.normalizeStaticRowIndexes((staticHeaders || []).map(cell => {
      cell.position = 'top';
      cell.rowspan = 1;
      return cell;
    }));
    this.staticFooters = bottomRows;
    this.syncStaticRowsColspan();
  }

  setStaticFooters(staticFooters) {
    this.staticFooters = this.normalizeStaticRowIndexes((staticFooters || []).map(cell => {
      cell.position = 'bottom';
      cell.rowspan = 1;
      return cell;
    }));
    this.syncStaticRowsColspan();
  }

  /**
   * A static row is a run of cells sharing `staticRowIndex`. Cells stored before static columns
   * existed carry none, so each of them becomes a row of its own — exactly what they were.
   */
  normalizeStaticRowIndexes(cells) {
    let next = 0;

    return cells.map(cell => {
      if (Number.isFinite(cell.staticRowIndex)) {
        next = Math.max(next, cell.staticRowIndex + 1);
      } else {
        cell.staticRowIndex = next++;
      }

      return cell;
    });
  }

  nextStaticRowIndex() {
    return this.getStaticRows().reduce((next, cell) => Math.max(next, (cell.staticRowIndex ?? -1) + 1), 0);
  }

  getStaticRows(position = null) {
    const allRows = [...(this.staticHeaders || []), ...(this.staticFooters || [])];
    if (!position) return allRows;

    return allRows.filter(cell => (cell.position || 'top') === position);
  }

  /**
   * The static cells grouped into the rows they render as.
   *
   * @return {TableCell[][]}
   */
  getStaticRowGroups(position = null) {
    return this.getStaticRows(position).reduce((groups, cell) => {
      const group = groups[groups.length - 1];
      const sameRow = group
        && Number.isFinite(cell.staticRowIndex)
        && group[0].staticRowIndex === cell.staticRowIndex
        && (group[0].position || 'top') === (cell.position || 'top');

      sameRow ? group.push(cell) : groups.push([cell]);

      return groups;
    }, []);
  }

  getStaticRowGroup(cellId) {
    return this.getStaticRowGroups().find(group => group.some(cell => cell.id === cellId));
  }

  getStaticRowColspan() {
    if (this.getTableColumnMode()) {
      const transposedColumns = this.rows.length + (this.headers.length && this.headerVisible !== false ? 1 : 0);
      return transposedColumns > 0 ? transposedColumns : 1;
    }

    // Columns, not cells: a merged cell in the header row covers more than one of them, and a
    // static row has to cover the same ground.
    const columns = cells => (cells || []).reduce((total, cell) => total + (cell.colspan || 1), 0);
    const regularColumns = columns(this.headers) || columns(this.rows[0]?.cells) || 1;

    return regularColumns > 0 ? regularColumns : 1;
  }

  /**
   * A static row's cells are cells of the table's own grid, so the row has to keep adding up to
   * the column count — otherwise it stops lining up with the columns underneath it. The split the
   * user made is preserved: only the surplus or shortfall is taken off the end of the row.
   */
  syncStaticRowsColspan() {
    // While loading, the static rows arrive before the body: with no columns to fit yet the sync
    // would collapse every span to 1 and hand the whole row to its last cell once the rows land.
    if (!this.rows.length && !this.headers.length) return;

    const colspan = this.getStaticRowColspan();
    const budgets = this.getStaticRowBudgets(colspan);

    this.getStaticRowGroups().forEach(group => {
      group.forEach(cell => {
        cell.colspan = Math.max(1, cell.colspan || 1);
        cell.rowspan = 1;
      });

      const columns = budgets.get(group[0].id) ?? colspan;
      let difference = columns - group.reduce((total, cell) => total + cell.colspan, 0);

      for (let index = group.length - 1; index >= 0 && difference !== 0; index--) {
        const fitted = Math.max(1, group[index].colspan + difference);

        difference -= fitted - group[index].colspan;
        group[index].colspan = fitted;
      }

      // More cells than columns: they cannot all shrink below one, so the row is left wider than
      // the table rather than silently dropping a cell the user put there.
      group[group.length - 1].colspan += Math.max(0, difference);
    });
  }

  /**
   * Which cell edges the hairline boost should paint, in the order `toHTML()` emits the cells, so
   * index N here is the Nth `<td>` in the markup. An edge is marked only where the cell across it
   * shares this one's background.
   *
   * @return {{right: boolean, bottom: boolean}[]}
   */
  getHairlineEdges() {
    const tableColumnMode = this.getTableColumnMode();
    const bodyCellRows = tableColumnMode
      ? this.getTransposedTableRows().map(row => row.cells)
      : null;
    // A header row with nothing to render emits no cells, so it is not in the markup and must not
    // be in this walk either.
    const headerRendered = !tableColumnMode && this.headers.length > 0 && this.headerVisible !== false;
    const rows = this.getVisualRows(bodyCellRows)
      .filter(row => row.kind !== 'header' || headerRendered);

    const grid = [];
    const occupied = {};
    const order = [];

    rows.forEach((row, rowIndex) => {
      let columnIndex = 0;

      row.cells.forEach(cell => {
        while (occupied[`${rowIndex}-${columnIndex}`]) columnIndex++;

        const colspan = cell.colspan || 1;
        const rowspan = cell.rowspan || 1;

        for (let r = 0; r < rowspan; r++) {
          grid[rowIndex + r] = grid[rowIndex + r] || [];

          for (let c = 0; c < colspan; c++) {
            grid[rowIndex + r][columnIndex + c] = cell;
            if (r || c) occupied[`${rowIndex + r}-${columnIndex + c}`] = true;
          }
        }

        order.push({ cell, rowIndex, columnIndex, colspan, rowspan });
        columnIndex += colspan;
      });
    });

    const background = cell => String(cell?.styles?.rules?.['background-color'] ?? '').toLowerCase();

    return order.map(({ cell, rowIndex, columnIndex, colspan, rowspan }) => {
      const mine = background(cell);
      let right = false;
      let bottom = false;

      for (let r = 0; r < rowspan; r++) {
        const neighbour = grid[rowIndex + r]?.[columnIndex + colspan];
        if (neighbour && background(neighbour) === mine) right = true;
      }

      for (let c = 0; c < colspan; c++) {
        const neighbour = grid[rowIndex + rowspan]?.[columnIndex + c];
        if (neighbour && background(neighbour) === mine) bottom = true;
      }

      return { right, bottom };
    });
  }

  /**
   * How many columns each static row has to itself. Normally all of them — but a rowspan started
   * in a body row carries into the rows underneath it, an anchored static row included, so that
   * row covers what is left instead of claiming a column that is already taken. Keyed by the
   * group's first cell id, because getStaticRowGroups() hands back fresh arrays each call.
   *
   * Column Mode is left alone: its rows are transposed on render, so this walk is not its grid.
   */
  getStaticRowBudgets(columns) {
    const budgets = new Map();

    if (this.getTableColumnMode()) {
      return budgets;
    }

    const occupied = {};

    this.getVisualRows().forEach((row, rowIndex) => {
      if (row.kind === 'static') {
        let taken = 0;

        for (let column = 0; column < columns; column++) {
          if (occupied[`${rowIndex}-${column}`]) taken++;
        }

        if (taken) budgets.set(row.cells[0].id, Math.max(1, columns - taken));

        return;
      }

      let column = 0;

      row.cells.forEach(cell => {
        while (occupied[`${rowIndex}-${column}`]) column++;

        const colspan = cell.colspan || 1;
        const rowspan = cell.rowspan || 1;

        for (let r = 1; r < rowspan; r++) {
          for (let c = 0; c < colspan; c++) occupied[`${rowIndex + r}-${column + c}`] = true;
        }

        column += colspan;
      });
    });

    return budgets;
  }

  setRows(rows) {
    this.rows = rows;
    this.syncStaticRowsColspan();
  }

  setHeaderVisibility(visible) {
    this.headerVisible = visible;
    this.syncStaticRowsColspan();
  }

  setMergeSameValues(mergeSameValues) {
    this.mergeSameValues = mergeSameValues;
  }

  /**
   * Whether a cell's Fixed Width means the whole cell or just its text. Off is the CSS default
   * (the width is the text area, and paddings and borders are added on top), which is how every
   * table built so far was laid out — so it stays off for them and on only where it was asked
   * for. On, a column set to 250 occupies 250, which is what people expect when they add column
   * widths up and compare them with another table.
   */
  setBorderBoxWidths(borderBoxWidths) {
    this.borderBoxWidths = !!borderBoxWidths;

    this.applyBorderBoxWidths();
  }

  // Cells are also added after the fact (new rows, new columns), so this runs on render rather
  // than only when the setting is changed.
  applyBorderBoxWidths() {
    this.getAllCells().forEach(cell => {
      if (this.borderBoxWidths) {
        cell.styles.setRule('box-sizing', 'border-box');
      } else {
        // Dropped rather than blanked, so a table that never used this is byte-identical.
        delete cell.styles.rules['box-sizing'];
      }
    });
  }

  getAllCells() {
    return this.rows.reduce(
      (acc, row) => [...acc, ...row.cells],
      [...this.headers, ...this.staticHeaders, ...this.staticFooters]
    );
  }

  setTruncateLongText(truncateLongText) {
    this.truncateLongText = !!truncateLongText;
  }

  setCellsRules(rules) {
    this.rows
      .reduce((acc, row) => [...acc, ...row.cells], [...this.headers, ...this.staticHeaders, ...this.staticFooters])
      .forEach(cell => {
        cell.styles.setRules(rules);
      });
  }

  /**
   * Cells that share the on-screen COLUMN of `cellId` while the table is
   * rendered transposed (Column Mode).
   *
   * Column Mode transposes the table (see getTransposedTableRows), so the
   * on-screen column of a cell is NOT its stored column:
   *  - a header is rendered in the left column together with every other header,
   *    so its on-screen column is all the headers;
   *  - a body cell of stored row j is rendered in one on-screen column together
   *    with the rest of row j, so its on-screen column is that whole body row.
   *
   * @param {string} cellId
   * @return {TableCell[]}
   */
  getColumnModeVisualColumnCells(cellId) {
    if (this.headers.some(cell => cell.id === cellId)) {
      return [...this.headers];
    }

    if (this.staticHeaders.some(cell => cell.id === cellId)) {
      return this.staticHeaders.filter(cell => cell.id === cellId);
    }

    if (this.staticFooters.some(cell => cell.id === cellId)) {
      return this.staticFooters.filter(cell => cell.id === cellId);
    }

    const row = this.rows.find(row => row.cells.some(cell => cell.id === cellId));

    return row ? [...row.cells] : [];
  }

  /**
   * Cells that share the on-screen ROW of `cellId` while the table is rendered
   * transposed (Column Mode). On screen, a row corresponds to a stored column
   * (its header plus that column's body cells).
   *
   * @param {string} cellId
   * @return {TableCell[]}
   */
  getColumnModeVisualRowCells(cellId) {
    let columnIndex = this.headers.findIndex(cell => cell.id === cellId);

    if (columnIndex < 0 && this.staticHeaders.some(cell => cell.id === cellId)) {
      return this.staticHeaders.filter(cell => cell.id === cellId);
    }

    if (columnIndex < 0 && this.staticFooters.some(cell => cell.id === cellId)) {
      return this.staticFooters.filter(cell => cell.id === cellId);
    }

    if (columnIndex < 0) {
      for (const row of this.rows) {
        const index = row.cells.findIndex(cell => cell.id === cellId);

        if (index >= 0) {
          columnIndex = index;
          break;
        }
      }
    }

    if (columnIndex < 0) {
      return [];
    }

    const cells = [];

    if (this.headers[columnIndex]) {
      cells.push(this.headers[columnIndex]);
    }

    this.rows.forEach(row => {
      if (row.cells[columnIndex]) {
        cells.push(row.cells[columnIndex]);
      }
    });

    return cells;
  }

  /**
   * Apply styles to the on-screen column of `cellId` (Column Mode).
   * Used so that horizontal controls (Width / Min Width) target the column the
   * user actually sees, not the stored (un-rotated) one.
   */
  applyColumnModeColumnStyle(cellId, rules) {
    this.getColumnModeVisualColumnCells(cellId).forEach(cell => cell.styles.setRules(rules));
  }

  /**
   * Apply styles to the on-screen row of `cellId` (Column Mode).
   * Used so that the vertical control (Height) targets the row the user sees.
   */
  applyColumnModeRowStyle(cellId, rules) {
    this.getColumnModeVisualRowCells(cellId).forEach(cell => cell.styles.setRules(rules));
  }

  setImage({ cells, src }) {
    const selectedCells = this.rows
      .reduce((acc, row) => [...acc, ...row.cells], [...this.headers, ...this.staticHeaders, ...this.staticFooters])
      .filter(cell => cells.includes(cell.id));
    
    selectedCells.forEach(cell => {
      cell.setSrc(src);
      cell.setValue('');
    });
  }

  setAutoFontSizeValue(autoFontSizeValue){
    this.autoFontSizeValue = autoFontSizeValue;
  }

  setAutoFontSize(autoFontSize){
    this.autoFontSize = autoFontSize;
  }

  getAutoFontSize(autoFontSize){
   return this.autoFontSize;
  }

  setTableColumnMode(tableColumnMode){
    this.tableColumnMode = tableColumnMode;
    this.syncStaticRowsColspan();
  }

  getTableColumnMode(){
   return this.columnModeEnabled && this.tableColumnMode && this.headers.length > 0; // When no headers we could get transposed rows as [] and blank table as a result
  }

  async getSize() {
    const $table = $(await this.toHTML());

    let $iframe = $('<iframe>', { style: 'position:absolute; visibility:hidden; width:0; height:0; border:none;' });
    const fonts = await this.getTableFonts(); // Contains base64-encoded fonts in <style>

    $iframe.appendTo('body');

    let iframeDoc = $iframe[0].contentDocument || $iframe[0].contentWindow.document;
    
    // Append a new blank <html> and <body> to the iframe
    iframeDoc.open();
    iframeDoc.write('<!DOCTYPE html><html><head></head><body></body></html>');
    iframeDoc.close();

    let $iframeHead = $(iframeDoc.head);
    let $iframeBody = $(iframeDoc.body);

    // Inject font styles into the iframe's head
    $iframeHead.append(fonts);

    // Append the table to the iframe's body
    $iframeBody.append($table);

    // Ensure images load
    await this.waitForImagesToLoad($table);

    // Wait for fonts to be fully applied
    await new Promise((resolve) => setTimeout(resolve, 100)); // Small delay to allow styles to apply

    // Alternative: Ensure all fonts are loaded before measuring
    if (iframeDoc.fonts && iframeDoc.fonts.ready) {
        await iframeDoc.fonts.ready;
    }

    // Reflow trick: Force browser to apply styles before measuring
    $iframeBody[0].offsetHeight;

    // Measure table dimensions inside the iframe
    let width = Math.ceil($table.width());
    let height = Math.ceil($table.height());

    // Clean up
    $iframe.remove();

    return { width, height };
  }

  setDimensions(width, height) {
    this.styles.setRules({
      width: `${width}px`,
      height: height === 'auto' ? height : `${height}px`,
    })
  }
  
  setHeaderStyles(rules = {}) {
    this.headerRowStyles.setRules(rules);
  }

  getStaticHeaderStyleAttribute() {
    const styles = this.headerRowStyles.getStyle(this.getTableColumnMode());
    return styles ? `style="${styles}"` : '';
  }

  addColumn(column) {
    this.headers.push(column);
  }

  addRow(row) {
    this.rows.push(row);
  }

  getColumnCount() {
    return Math.max(this.headers.length, ...this.rows.map(row => row.cells.length), 0);
  }

  addNewColumn(index = null) {
    const columnCount = this.getColumnCount();

    index = index ?? (this.headers.length || columnCount);

    // A header row the user removed stays removed; a table left with no columns at all gets it back.
    if (this.headers.length || !columnCount) {
      const cell = new TableBuilder.TableCell(DEFAUL_COLUMN_TITLE);
      const previous = this.headers[index - 1] || this.headers[index];

      if (previous) {
        cell.styles.setRules(previous.getStyles());
      }

      this.headers.splice(index, 0, cell);
    }

    for (let i = 0; i < this.rows.length; i++) {
      const cell = new TableBuilder.TableCell(DEFAUL_CELL_VALUE);
      const previous = this.rows[i].cells[index - 1] || this.rows[i].cells[index];

      if (previous) {
        cell.styles.setRules(previous.getStyles());
      }

      this.rows[i].cells.splice(index, 0, cell);
    }

    this.shiftAnchorsForColumnChange(index, 1);
    this.syncStaticRowsColspan();
  }
  
  addStaticHeaderCell(index = null) {
    if (!this.staticHeaders) {
      this.staticHeaders = [];
    }

    const colCount = this.getStaticRowColspan();
    index = index !== null ? index : this.staticHeaders.length;

    const cell = new TableBuilder.TableCell(DEFAUL_COLUMN_TITLE);
    const neighbour = this.staticHeaders[index - 1] || this.staticHeaders[index];

    if (neighbour) {
      const styles = neighbour.getStyles();
      cell.styles.setRules(styles);
    } else if (this.headers[0]) {
      const styles = this.headers[0].getStyles();
      cell.styles.setRules(styles);
    } else if (this.rows[0]?.cells[0]) {
      const styles = this.rows[0].cells[0].getStyles();
      cell.styles.setRules(styles);
    }
    let extraRules = {
      'border-top-style': 'solid',
      'border-bottom-style': 'solid',
      'border-left-style': 'solid',
      'border-right-style': 'solid',
      'border-width': '2px',
    }
    
    cell.styles.setRules(extraRules);

    cell.position = 'top';
    cell.rowspan = 1;
    cell.colspan = colCount;
    cell.staticRowIndex = this.nextStaticRowIndex();

    this.staticHeaders.splice(index, 0, cell);
    this.syncStaticRowsColspan();
    
    if (!this.tableColumnMode) {
      this.setHeaderStyles({'height': '40px'});
    }

    return cell;
  }

  /**
   * A static row inside the table rather than in the band above it: it renders straight after the
   * body row it is anchored to (-1 = straight under the header row).
   */
  addStaticRowAnchoredTo(bodyRowIndex) {
    const cell = this.addStaticHeaderCell();

    if (cell) {
      cell.afterRowIndex = bodyRowIndex;
    }

    return cell;
  }

  /**
   * A new static row right before (offset -1) or after (offset 0) the static row a cell belongs to:
   * same band or anchor, same look, so a band can be grown from one of its own rows.
   */
  addStaticRowNextTo(cellId, offset) {
    const group = this.getStaticRowGroup(cellId);

    if (!group) return null;

    const bottom = (group[0].position || 'top') === 'bottom';
    const target = bottom ? this.staticFooters : this.staticHeaders;
    const first = target.indexOf(group[0]);
    const last = target.indexOf(group[group.length - 1]);
    const index = offset < 0 ? first : last + 1;
    const cell = bottom ? this.addStaticFooterCell(index) : this.addStaticHeaderCell(index);

    if (cell) {
      cell.styles.setRules(group[0].getStyles());
      cell.afterRowIndex = group[0].afterRowIndex ?? null;
    }

    return cell;
  }

  addStaticFooterCell(index = null) {
    if (!this.staticFooters) {
      this.staticFooters = [];
    }

    const colCount = this.getStaticRowColspan();
    index = index !== null ? index : this.staticFooters.length;

    const cell = new TableBuilder.TableCell(DEFAUL_COLUMN_TITLE);

    if (this.staticFooters[index - 1]) {
      const styles = this.staticFooters[index - 1].getStyles();
      cell.styles.setRules(styles);
    } else if (this.headers[0]) {
      const styles = this.headers[0].getStyles();
      cell.styles.setRules(styles);
    } else if (this.rows[0]?.cells[0]) {
      const styles = this.rows[0].cells[0].getStyles();
      cell.styles.setRules(styles);
    }

    cell.styles.setRules({
      'border-top-style': 'solid',
      'border-bottom-style': 'solid',
      'border-left-style': 'solid',
      'border-right-style': 'solid',
      'border-width': '2px',
    });

    cell.position = 'bottom';
    cell.rowspan = 1;
    cell.colspan = colCount;
    cell.staticRowIndex = this.nextStaticRowIndex();

    this.staticFooters.splice(index, 0, cell);
    this.syncStaticRowsColspan();
  }

  /**
   * Takes the last column of the selected static cell and gives it to a new cell next to it. The
   * split therefore always lands on a column boundary of the table below, which is the point: a
   * static cell is a cell of the same grid, so it lines up with the columns it sits over.
   */
  addStaticColumnCell(cellId) {
    const sourceCell = this.getStaticRows().find(cell => cell.id === cellId);

    // Nothing left to give: a cell over a single column cannot be split.
    if (!sourceCell || (sourceCell.colspan || 1) < 2) return null;

    const target = sourceCell.position === 'bottom' ? this.staticFooters : this.staticHeaders;
    const cell = new TableBuilder.TableCell(DEFAUL_COLUMN_TITLE);

    // Replaces the constructor's defaults rather than merging over them, so the two halves of a
    // split row look the same even where the cell being split has no rule of its own.
    cell.styles.rules = { ...sourceCell.getStyles() };
    cell.position = sourceCell.position;
    cell.staticRowIndex = sourceCell.staticRowIndex;
    cell.rowspan = 1;
    cell.colspan = 1;

    // The new cell is one column wide, so its width is that column's — the cell it was split from
    // keeps the width it had for the columns it still covers.
    delete cell.styles.rules['width'];
    delete cell.styles.rules['min-width'];

    sourceCell.colspan -= 1;

    target.splice(target.findIndex(_cell => _cell.id === cellId) + 1, 0, cell);
    this.syncStaticRowsColspan();

    return cell;
  }

  /**
   * Moves the boundary between a static cell and the next one along. The row has to keep covering
   * the table, so the columns this cell gains or gives up come from its neighbour — which is what
   * dragging the boundary means. Growing past what the neighbour can give stops at its last column.
   */
  setStaticCellColspan(cellId, colspan) {
    const group = this.getStaticRowGroup(cellId);
    const index = group ? group.findIndex(cell => cell.id === cellId) : -1;

    if (index < 0) return;

    const cell = group[index];
    // The last cell has nothing on its right to trade with, so it trades with the one before it.
    const neighbour = group[index + 1] || group[index - 1];

    if (!neighbour) return;

    const wanted = Math.max(1, Number(colspan) || 1);
    const total = cell.colspan + neighbour.colspan;
    const fitted = Math.min(wanted, total - 1);

    neighbour.colspan = total - fitted;
    cell.colspan = fitted;
  }

  /**
   * Removes a cell from its static row and hands its columns to a neighbour, so the row still
   * covers the table.
   */
  removeStaticColumnCell(cellId) {
    const group = this.getStaticRowGroup(cellId);

    // The last cell of a static row is the row itself — that is Remove Row's job.
    if (!group || group.length < 2) return;

    const index = group.findIndex(cell => cell.id === cellId);
    const neighbour = group[index - 1] || group[index + 1];

    neighbour.colspan = (neighbour.colspan || 1) + (group[index].colspan || 1);

    this.staticHeaders = this.staticHeaders.filter(cell => cell.id !== cellId);
    this.staticFooters = this.staticFooters.filter(cell => cell.id !== cellId);
    this.syncStaticRowsColspan();
  }

  removeStaticRowGroup(group) {
    const ids = new Set((group || []).map(cell => cell.id));

    this.staticHeaders = this.staticHeaders.filter(cell => !ids.has(cell.id));
    this.staticFooters = this.staticFooters.filter(cell => !ids.has(cell.id));
  }

  addNewRow(index = null) {
    index = index ?? this.rows.length;

    const row = new TableBuilder.TableRow();
    const reference = this.rows[index - 1] || this.rows[0];

    // The new row copies the reference row's shape on the column grid, not its cell count: a
    // merged cell gives a cell of the same span, and a cell spanning down past the reference row
    // grows by one row instead of leaving a hole beside it.
    const occupied = {};
    const grid = [];

    this.rows.forEach((bodyRow, rowIndex) => {
      let column = 0;

      bodyRow.cells.forEach(cell => {
        while (occupied[`${rowIndex}-${column}`]) column++;

        const colspan = cell.colspan || 1;
        const rowspan = cell.rowspan || 1;

        for (let r = 0; r < rowspan; r++) {
          grid[rowIndex + r] = grid[rowIndex + r] || [];

          for (let c = 0; c < colspan; c++) {
            grid[rowIndex + r][column + c] = { cell, colspan, endsHere: r === rowspan - 1 };
            if (r || c) occupied[`${rowIndex + r}-${column + c}`] = true;
          }
        }

        column += colspan;
      });
    });

    const slots = reference ? grid[this.rows.indexOf(reference)] || [] : [];
    const columns = slots.length || this.headers.length;

    for (let column = 0; column < columns;) {
      const slot = slots[column];

      if (slot && !slot.endsHere && index > this.rows.indexOf(reference)) {
        slot.cell.rowspan = (slot.cell.rowspan || 1) + 1;
        column += slot.colspan;
        continue;
      }

      const cell = new TableBuilder.TableCell(DEFAUL_CELL_VALUE);

      if (slot) {
        cell.styles.setRules(slot.cell.getStyles());
        cell.colspan = slot.colspan;
      }

      row.addCell(cell);
      column += slot ? slot.colspan : 1;
    }

    this.rows.splice(index, 0, row);
    this.shiftAnchorsForRowChange(index, 1);
    this.syncStaticRowsColspan();
  }

  removeNewRow(index = null) {
    const visualRows = this.getVisualRows();

    // Legacy fallback: when no index is provided, remove the last body row.
    if (index === null || index < 0) {
      index = visualRows.reduce((last, row, at) => (row.kind === 'body' ? at : last), -1);
    }

    const row = visualRows[index];

    if (!row) {
      return;
    }

    if (row.kind === 'static') {
      this.removeStaticRowGroup(row.cells);
    } else if (row.kind === 'header') {
      this.headers = [];
    } else {
      this.rows.splice(row.index, 1);
      this.shiftAnchorsForRowChange(row.index, -1);
    }

    this.syncStaticRowsColspan();
  }

  /**
   * Anchored static rows follow the body row they were put under when rows are added or removed
   * above them — otherwise they would drift to a different place in the table.
   */
  shiftStaticRowAnchors(fromIndex, delta) {
    this.getStaticRows().forEach(cell => {
      if (Number.isFinite(cell.afterRowIndex) && cell.afterRowIndex >= fromIndex) {
        cell.afterRowIndex += delta;
      }
    });
  }

  // `afterRowIndex` is an on-screen row, and Column Mode renders a stored column as one.
  shiftAnchorsForRowChange(fromIndex, delta) {
    if (!this.getTableColumnMode()) {
      this.shiftStaticRowAnchors(fromIndex, delta);
    }
  }

  shiftAnchorsForColumnChange(fromIndex, delta) {
    if (this.getTableColumnMode()) {
      this.shiftStaticRowAnchors(fromIndex, delta);
    }
  }
  
  removeRows(selectedCells = []) {
    if (!selectedCells.length) {
      this.removeNewRow();
      return;
    }

    this.getRowIndicesToRemove(selectedCells)
      .sort((a, b) => b - a)
      .forEach(index => this.removeNewRow(index));
  }

  getRowIndicesToRemove(selectedCells) {
    const rows = this.getRows();
    const indicesToRemove = new Set();

    selectedCells.forEach(cell => {
      const index = rows.findIndex(row => row.some(_cell => _cell.id === cell.id));

      if (index >= 0) {
        indicesToRemove.add(index);
      }
    });

    return [...indicesToRemove];
  }

  canRemoveRows(selectedCells = []) {
    const visualRows = this.getVisualRows();
    const bodyRowsToRemove = selectedCells.length
      ? this.getRowIndicesToRemove(selectedCells).filter(index => visualRows[index].kind === 'body').length
      : 1;

    return this.rows.length - bodyRowsToRemove >= 1;
  }

  removeNewColumn(index = null) {
    index = index !== null && index >= 0
      ? index 
      : this.headers.length - 1;
    
    this.headers.splice(index, 1);

    for (let i = 0; i < this.rows.length; i++) {
      this.rows[i].cells.splice(index, 1);
    }

    this.shiftAnchorsForColumnChange(index, -1);
    this.syncStaticRowsColspan();
  }
  
  removeColumns(selectedCells = []) {
    if (!selectedCells.length) {
      this.removeNewColumn();
      return;
    }

    // A static row has its own columns, unrelated to the table's — removing one of them must not
    // take a table column with it.
    const staticIds = new Set(this.getStaticRows().map(cell => cell.id));

    selectedCells
      .filter(cell => staticIds.has(cell.id))
      .forEach(cell => this.removeStaticColumnCell(cell.id));

    // Delete indices in descending order to avoid displacement
    this.getColumnIndicesToRemove(selectedCells)
      .sort((a, b) => b - a)
      .forEach(index => this.removeNewColumn(index));
  }

  getColumnIndicesToRemove(selectedCells) {
    const staticIds = new Set(this.getStaticRows().map(cell => cell.id));
    const rows = this.getRows();
    const indicesToRemove = new Set();

    selectedCells
      .filter(cell => !staticIds.has(cell.id))
      .forEach(cell => {
        rows.forEach(row => {
          const index = row.findIndex(_cell => _cell.id === cell.id);

          if (index >= 0) {
            indicesToRemove.add(index);
          }
        });
      });

    return [...indicesToRemove];
  }

  canRemoveColumns(selectedCells = []) {
    const columnsToRemove = selectedCells.length ? this.getColumnIndicesToRemove(selectedCells).length : 1;

    return !columnsToRemove || this.getColumnCount() - columnsToRemove >= 1;
  }

  moveColumnTo(arr, fromIndex, toIndex) {
    if (Array.isArray(arr)) {
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= arr.length || toIndex >= arr.length) {
        return;
      }
      const [movedCell] = arr.splice(fromIndex, 1);
      arr.splice(toIndex, 0, movedCell);
    }
  }

  moveColumn(action, selectedCell) {
    const rows = [this.headers, ...this.rows.map(row => row.cells)];
    let fromIndex = null;
    const indices = [];

    rows.forEach(row => {
      const index = row.findIndex(_cell => _cell.id === selectedCell.id);

      if (index >= 0) {
        fromIndex = index;
      }
    });

    rows.forEach(row => {
      const numOfColspan = row.reduce((acc, item) => acc + item.colspan, 0);
      const hasColspan = row.length < numOfColspan;
      const diff = hasColspan ? numOfColspan - row.length : 0;

      const _fromIndex = fromIndex - diff;
      const _toIndex = action === 'left' ? _fromIndex - 1 : _fromIndex + 1;
      
      indices.push({
        fromIndex: _fromIndex,
        toIndex: _toIndex
      });
      
    });
    
    indices.forEach((el, i) => this.moveColumnTo(rows[i], el.fromIndex, el.toIndex));
    this.syncStaticRowsColspan();
  }

  moveRow(action, selectedCell) {
    const rowIndex = this.rows.findIndex(row =>
      Array.isArray(row.cells) && row.cells.some(cell => cell.id === selectedCell.id)
    );

    const row = this.rows[rowIndex];
    const rowSpan = Math.max(...row.cells.map(cell => cell.rowspan || 1));
    const fromStart = rowIndex;
    const fromEnd = rowIndex + rowSpan - 1;

    let toIndex = action === 'up'
        ? fromStart - 1
        : fromEnd + 1;

    if (toIndex < 0 || toIndex >= this.rows.length) {
      return;
    }

    const blockToMove = this.rows.splice(fromStart, rowSpan);

    this.rows.splice(toIndex - rowSpan + 1, 0, ...blockToMove);
    this.syncStaticRowsColspan();
  }

  createTableGrid(columns = 10, rows = 10) {
    this.headers = [];
    this.staticHeaders = [];
    this.staticFooters = [];
    this.rows = [];

    for (let i = 0; i < columns; i++) {
      this.addColumn(new TableBuilder.TableCell(DEFAUL_COLUMN_TITLE));
    }

    for (let i = 0; i < rows; i++) {
      const row = new TableBuilder.TableRow();

      for (let i = 0; i < columns; i++) {
        row.addCell(new TableBuilder.TableCell('Value'));
      }

      this.addRow(row);
    }
    
    const cellsRules = [
      { rule: 'font-family', value: this.styles.rules['font-family'], measure: '' },
      { rule: 'font-size', value: this.styles.rules['font-size'], measure: '' },
      { rule: 'text-align', value: this.styles.rules['text-align'], measure: '' },
      { rule: 'color', value: this.styles.rules['color'], measure: '' },
    ];

    this.changeMultipleCellsStyle(cellsRules, APPLY_TO_VALUES.ALL);
    this.syncStaticRowsColspan();
  }

  addCellRow(index = null) {
    index = index ? index : this.headers.length;

    for (let i = 0; i < this.rows.length; i++) {
      const cell = new TableBuilder.TableCell(DEFAUL_CELL_VALUE);
      this.rows[i].cells.splice(index, 0, cell);
    }
  }
  
  addCellHeader(index = null) {
    const cell = new TableBuilder.TableCell(DEFAUL_COLUMN_TITLE);
    this.headers.splice(index, 0, cell);
  }
  
  getStyle(id, key, defaultValue, applyTo = APPLY_TO_BORDERS_VALUES.ALL) {
    switch(true) {
      case CELL_STYLE_KEYS.includes(key):
        return this.getCellStyle(id, key, defaultValue);
      case ROW_STYLE_KEYS.includes(key):
        return this.getRowStyle(id, key, defaultValue);
      case CELL_BORDER_STYLE_KEYS.includes(key):
        return this.getCellBorderStyle(id, key, defaultValue, applyTo);
      default:
        return this.getCellStyle(id, key, defaultValue);
    }
  }
  
  getCellStyle(id, key, defaultValue) {
    const cell = this.rows
      .reduce((acc, row) => [...acc, ...row.cells], [...this.staticHeaders, ...this.staticFooters, ...this.headers])
      .find(cell => cell.id === id);
   
    if (!cell) return defaultValue;

    const value = cell.styles.rules[key];
    const number = parseInt(value);

    return value === undefined ? defaultValue : Number.isNaN(number) ? value : number;
  }
  
  /**
   * The mapping names the cells carry, in the order they are read, deduplicated. This is what the
   * import modal offers to map a feed column to, alongside the table's own columns.
   *
   * @return {string[]}
   */
  getCellMappingNames() {
    const names = this.getVisualRows()
      .reduce((acc, row) => [...acc, ...row.cells], [])
      .map(cell => cell.getMappingName())
      .filter(Boolean);

    return [...new Set(names)];
  }

  getCellById(id) {
    return this.rows
      .reduce((acc, row) => [...acc, ...row.cells], [...this.staticHeaders, ...this.staticFooters, ...this.headers])
      .find(cell => cell.id === id);
  }

  applyAngleFit(id, { angle, width, height }) {
    const cell = this.getCellById(id);

    if (!cell) return;

    cell.styles.setRules({
      [CELL_ANGLE_FITTED_RULE]: angle,
      [CELL_ANGLE_FIT_WIDTH_RULE]: width,
      [CELL_ANGLE_FIT_HEIGHT_RULE]: height,
    });

    const transposed = this.getTableColumnMode();
    const rowHeight = this.getAngleFitRowHeight(id, height);

    if (this.tableColumnMode) {
      transposed
        ? this.applyColumnModeRowStyle(id, { height: `${rowHeight}px` })
        : this.applyColumnModeColumnStyle(id, { height: `${rowHeight}px` });
    } else {
      this.changeRowStyles({ cells: [id], rules: [{ rule: 'height', value: rowHeight, measure: 'px' }] });
    }

    transposed
      ? this.applyColumnModeColumnStyle(id, { 'min-width': `${width}px` })
      : this.changeCellStyle({ cells: [id], rules: [{ rule: 'min-width', value: width, measure: 'px' }] });
  }

  // Rotated text is out of flow, so it cannot push its row taller: fitting one cell must not
  // drop the row below what the other rotated cells sharing it still need.
  getAngleFitRowHeight(id, height) {
    const cells = this.tableColumnMode
      ? (this.getTableColumnMode() ? this.getColumnModeVisualRowCells(id) : this.getColumnModeVisualColumnCells(id))
      : this.getRow(this.getCellById(id));

    return cells.reduce(
      (max, cell) => Math.max(max, parseFloat(cell.styles.getRule(CELL_ANGLE_FIT_HEIGHT_RULE)) || 0),
      height,
    );
  }

  getCellSrc(id) {
    const cell = this.rows
      .reduce((acc, row) => [...acc, ...row.cells], [...this.staticHeaders, ...this.staticFooters, ...this.headers])
      .find(cell => cell.id === id);
    
    if (!cell) return;
    
    return cell.getSrc();
  }

  getCellDotLeaderEnabled(id, defaultValue = false) {
    const cell = this.rows
      .reduce((acc, row) => [...acc, ...row.cells], [...this.staticHeaders, ...this.staticFooters, ...this.headers])
      .find(_cell => _cell.id === id);

    if (!cell) {
      return defaultValue;
    }

    return cell.getDotLeaderEnabled();
  }

  getRowStyle(id, key, defaultValue) {
    const staticGroup = this.getStaticRowGroup(id);
    // Static rows carry their height on their cells; headerRowStyles is only what rows saved
    // before that still have.
    const staticValue = staticGroup?.reduce((value, cell) => cell.styles.rules[key] ?? value, undefined);

    const rowStyles = this.rows
      .find(_row => _row.cells.find(_cell => _cell.id === id))?.styles
      || this.headerRowStyles;

    if (!rowStyles) return defaultValue;

    const value = staticValue ?? rowStyles.rules[key];
    const number = parseInt(value);

    return value === undefined ? defaultValue : Number.isNaN(number) ? value : number;
  }

  getCellBorderStyle(id, rule, defaultValue, applyTo) {
    const cell = this.rows
      .reduce((acc, row) => [...acc, ...row.cells], [...this.staticHeaders, ...this.staticFooters, ...this.headers])
      .find(cell => cell.id === id);
    
    let value;
    
    switch(applyTo) {
      case APPLY_TO_BORDERS_VALUES.ALL:
      case APPLY_TO_BORDERS_VALUES.INNER:
      case APPLY_TO_BORDERS_VALUES.OUTER:
        value = cell.styles.rules[BORDER_STYLES[rule][BORDERS.TOP]] || cell.styles.rules[rule];
        break;
      case APPLY_TO_BORDERS_VALUES.RIGHT:
        value = cell.styles.rules[BORDER_STYLES[rule][BORDERS.RIGHT]];
        break;
      case APPLY_TO_BORDERS_VALUES.LEFT:
        value = cell.styles.rules[BORDER_STYLES[rule][BORDERS.LEFT]];
        break;
      case APPLY_TO_BORDERS_VALUES.TOP:
        value = cell.styles.rules[BORDER_STYLES[rule][BORDERS.TOP]];
        break;
      case APPLY_TO_BORDERS_VALUES.BOTTOM:
        value = cell.styles.rules[BORDER_STYLES[rule][BORDERS.BOTTOM]];
        break;
      default:
        value = defaultValue;
    }

    const number = parseInt(value);
    
    return value === undefined 
      ? defaultValue 
      : Number.isNaN(number) ? value : number;
  }

  changeRowStyles(params) {
    const rules = params.rules.reduce((acc, param) => ({
      ...acc,
      [param.rule]: `${param.value}${param.measure}`
    }), {});

    if (this.headers.some(cell => params.cells.includes(cell.id))) {
      this.headerRowStyles.setRules(rules);
    }

    // Kept on the static row's own cells: headerRowStyles is shared with the header row and with
    // every other static row, so writing there made one static row's height everyone else's.
    this.getStaticRowGroups()
      .filter(group => group.some(cell => params.cells.includes(cell.id)))
      .forEach(group => group.forEach(cell => cell.styles.setRules(rules)));

    const rows = this.rows.reduce((acc, _row) => {
      const cellIndex = _row.cells.findIndex(cell => params.cells.includes(cell.id));
      
      if (cellIndex >= 0) {
        return [...acc, _row];
      }
      
      return acc;
    }, []);
    
    rows.forEach(_row => _row.styles.setRules(rules));
  }
  
  changeCellStyle(cellParams) {
    const rules = cellParams.rules.reduce((acc, param) => ({
      ...acc,
      [param.rule]: `${param.value}${param.measure}`
    }), {});
    
    let cells = rules['width'] || rules['min-width'] 
      ? this.getCellsByColumns(cellParams.cells) 
      : this.rows
        .reduce((acc, row) => [...acc, ...row.cells], [...this.headers, ...this.staticHeaders, ...this.staticFooters])
        .filter(cell => cellParams.cells.includes(cell.id));
    
    if (!cells) return;
    
    cells.forEach(cell => {
      cell.styles.setRules(rules);
    });
  }

  /**
   * Parse CSS-like padding shorthand from user input:
   * "1" | "1 2" | "1 2 3" | "1 2 3 4"
    * Applies to ALL cells (headers + staticHeaders + staticFooters + body).
   *
   * @param {string|number} input
   * @param {string} measure - "px" by default
    * @param {{ scope?: "all"|"body"|"headers"|"staticHeaders"|"staticFooters", clampMax?: number }} opts
   */
  changeTablePaddingFromShorthand(input, measure = "px", opts = {}) {
    const scope = opts.scope || "all";
    const clampMax = Number.isFinite(opts.clampMax) ? opts.clampMax : 80;

    const parsed = this.parsePaddingShorthand(input);
    if (!parsed) return;

    const clamp = (n) => Math.max(0, Math.min(clampMax, Math.round(n)));

    const rules = {
      "padding-top": `${clamp(parsed.top)}${measure}`,
      "padding-right": `${clamp(parsed.right)}${measure}`,
      "padding-bottom": `${clamp(parsed.bottom)}${measure}`,
      "padding-left": `${clamp(parsed.left)}${measure}`,
    };

    // choose cells by scope
    const bodyCells = this.rows.reduce((acc, row) => [...acc, ...row.cells], []);
    const headerCells = [...this.headers];
    const staticHeaderCells = [...this.staticHeaders];
    const staticFooterCells = [...this.staticFooters];

    let cells = [];
    if (scope === "body") cells = bodyCells;
    else if (scope === "headers") cells = headerCells;
    else if (scope === "staticHeaders") cells = staticHeaderCells;
    else if (scope === "staticFooters") cells = staticFooterCells;
    else cells = [...headerCells, ...staticHeaderCells, ...bodyCells, ...staticFooterCells];

    if (!cells?.length) return;

    cells.forEach((cell) => cell.styles.setRules(rules));

    this.defaultCellPaddingShorthand = input;
  }

  /**
   * Returns {top,right,bottom,left} or null.
   * Accepts number or string (can include px, etc; we extract numbers).
   *
   * @param {string|number} input
   * @returns {{top:number,right:number,bottom:number,left:number}|null}
   */
  parsePaddingShorthand(input) {
    if (input === null || input === undefined) return null;

    const raw = String(input).trim();
    if (!raw) return null;

    // split by whitespace; allow "10px" / "10.5" etc. Extract numeric part.
    const parts = raw.split(/\s+/).slice(0, 4);

    const nums = parts
      .map((p) => {
        const m = p.match(/^(-?\d+(\.\d+)?)/); // read leading number
        return m ? parseFloat(m[1]) : NaN;
      })
      .filter((n) => Number.isFinite(n));

    // Must be 1..4 values. If user typed junk -> null.
    if (nums.length < 1 || nums.length > 4) return null;

    let top, right, bottom, left;

    if (nums.length === 1) {
      top = right = bottom = left = nums[0];
    } else if (nums.length === 2) {
      top = bottom = nums[0];
      right = left = nums[1];
    } else if (nums.length === 3) {
      top = nums[0];
      right = left = nums[1];
      bottom = nums[2];
    } else {
      top = nums[0];
      right = nums[1];
      bottom = nums[2];
      left = nums[3];
    }

    return { top, right, bottom, left };
  }

  getCellsForApplyTo(applyTo = APPLY_TO_VALUES.ALL, selectedCell = null) {
    switch (true) {
      case applyTo === APPLY_TO_VALUES.ALL:
        return this.rows.reduce((acc, row) => [...acc, ...row.cells], [...this.headers, ...this.staticHeaders, ...this.staticFooters]);
      case applyTo === APPLY_TO_VALUES.HEADER:
        return this.headers;
      case applyTo === APPLY_TO_VALUES.BODY:
        return this.rows.reduce((acc, row) => [...acc, ...row.cells], []);
      case applyTo === APPLY_TO_VALUES.COLUMN: {
        if (this.getTableColumnMode()) {
          return this.getColumnModeVisualColumnCells(selectedCell?.id);
        }

        // Grid columns, not cell indexes: header, static and body cells share the grid, and a span
        // in any row shifts every index after it.
        const columnByCellId = this.getColumnByCellId();
        const column = columnByCellId.get(selectedCell?.id);

        if (column === undefined) {
          return [];
        }

        return this.rows.reduce((acc, row) => [...acc, ...row.cells.filter(cell => columnByCellId.get(cell.id) === column)], []);
      }
      case applyTo === APPLY_TO_VALUES.ALTERNATING_ROWS: {
        let rows = this.rows;

        // POR-11552 - Maybe we'd need to apply the same approach for some other cases if needed
        if (this.getTableColumnMode() && this.headers.length) {
          rows = this.getTransposedTableRows();
        }

        let currentRow = rows.findIndex(row => {
          return row.cells.find(cell => cell.id === selectedCell?.id);
        });

        if (currentRow < 0) {
          return [];
        }

        // Determine if the odd rows (1) or even (0) should be formatted
        currentRow = currentRow % 2 === 0 ? 0 : 1;

        return rows.reduce((acc, row, index) => {
          if (index === currentRow) {
            currentRow += 2;
            return [...acc, ...row.cells];
          }

          return acc;
        }, []);
      }
      default:
        return this.rows.reduce((acc, row) => [...acc, ...row.cells], [...this.headers, ...this.staticHeaders, ...this.staticFooters]);
    }
  }

  changeMultipleCellsStyle(styleParams, applyTo = APPLY_TO_VALUES.ALL, selectedCell = null) {
    const rules = styleParams.reduce((acc, param) => {
      if (!ALLOWED_STYLES_TO_APPLY_TO_ALL.includes(param.rule)) {
        return acc;
      }

      return {
        ...acc,
        [param.rule]: `${param.value}${param.measure}`
      }
    }, {});

    const cells = this.getCellsForApplyTo(applyTo, selectedCell);
    
    cells.forEach(cell => {
      cell.styles.setRules(rules);
    });
  }

  changeMultipleCellsDotLeader(enabled, applyTo = APPLY_TO_VALUES.SELECTED, selectedCell = null) {
    const cells = applyTo === APPLY_TO_VALUES.SELECTED
      ? this.rows
        .reduce((acc, row) => [...acc, ...row.cells], [...this.headers, ...this.staticHeaders, ...this.staticFooters])
        .filter(cell => selectedCell && cell.id === selectedCell.id)
      : this.getCellsForApplyTo(applyTo, selectedCell);

    cells.forEach(cell => {
      cell.setDotLeaderEnabled(enabled);
    });
  }

  changeCellBorderStyle({ cells, applyTo, handler, styleRule }) {
    let rows;
    
    switch (true) {
      case applyTo === APPLY_TO_VALUES.SELECTED:
        rows = this.getRows()
          .map(row => row.filter(cell => cells.includes(cell.id)))
          .filter(row => row.length);
        break;
      case applyTo === APPLY_TO_VALUES.ALL:
        rows = this.getRows();
        break;
      case applyTo === APPLY_TO_VALUES.HEADER:
        rows = [this.headers];
        break;
      case applyTo === APPLY_TO_VALUES.BODY:
        rows = this.rows.reduce((acc, row) => ([...acc, row.cells]), []);
        break;
      case applyTo === APPLY_TO_VALUES.COLUMN: {
        let column = null;

        this.rows.forEach(row => {
          const index = row.cells.findIndex(cell => cell.id === cells[0]);

          if (index >= 0) {
            column = index;
          }
        });

        rows = this.rows.reduce((acc, row) => [...acc, [row.cells[column]]], []);
        break;
      }
      case applyTo === APPLY_TO_VALUES.ALTERNATING_ROWS: {
        let currentRow = this.rows.findIndex(row => {
          return row.cells.find(cell => cell.id === cells[0]);
        });

        // Determine if the odd rows (1) or even (0) should be formatted
        currentRow % 2 === 0
          ? currentRow = 0
          : currentRow = 1;

        rows = this.rows.reduce((acc, row, index) => {
          if (index === currentRow) {
            currentRow = currentRow + 2;

            return [...acc, row.cells];
          }

          return acc;
        }, []);

        break;
      }
      default:
        rows = this.getRows()
          .map(row => row.filter(cell => cells.includes(cell.id)))
          .filter(row => row.length);
    }
    
    this[handler](rows, styleRule);
  }
  
  setOuterBorders(rows, { rule, value, measure }) {
    const allRows = this.getRows();
    
    const groupedCells = groupConnectedCells(allRows, rows);
    
    const _value = value + measure;
    
    groupedCells.forEach(group => {
      group.forEach((row, index) => {
        row.forEach((cell, index, arr) => {
          if (index === 0) {
            cell.styles.setRules({
              [BORDER_STYLES[rule][BORDERS.LEFT]]: _value,
            });
          }
          
          if (index === arr.length - 1) {
            cell.styles.setRules({
              [BORDER_STYLES[rule][BORDERS.RIGHT]]: _value
            });
          }

          if (this.headers.find(headerCell => headerCell.id === cell.id)) {
            cell.styles.setRules({
              [BORDER_STYLES[rule][BORDERS.TOP]]: _value
            });
          }
        });

        if (index === 0) {
          row.forEach(cell => cell.styles.setRules({
            [BORDER_STYLES[rule][BORDERS.TOP]]: _value
          }));
        }

        if (index === group.length - 1) {
          row.forEach(cell => cell.styles.setRules({
            [BORDER_STYLES[rule][BORDERS.BOTTOM]]: _value
          }));
        }
      });
    });

    this.changeNeighborsCellsStyle(rows.flatMap(row => row.map(cell => cell.id)), { rule, value, measure });
  }
  
  setInnerBorders(rows, { rule, value, measure }) {
    const _value = value + measure;
    
    rows.forEach((row, index) => {
      row.forEach((cell, index, array) => {
        if (index !== array.length - 1) {
          cell.styles.setRules({
            [BORDER_STYLES[rule][BORDERS.RIGHT]]: _value
          });
        }

        if (index > 0) {
          cell.styles.setRules({
            [BORDER_STYLES[rule][BORDERS.LEFT]]: _value
          });
        }
      });

      if (index !== rows.length - 1) {
        row.forEach(cell => cell.styles.setRules({
          [BORDER_STYLES[rule][BORDERS.BOTTOM]]: _value
        }));
      }

      if (index > 0) {
        row.forEach(cell => cell.styles.setRules({
          [BORDER_STYLES[rule][BORDERS.TOP]]: _value
        }));
      }
    });
  }
  
  setAllBorders(rows, { rule, value, measure }) {
    const _value = value + measure;
    
    rows.forEach(row => {
      row.forEach(cell => {
        cell.styles.setRules({
          [BORDER_STYLES[rule][BORDERS.TOP]]: _value,
          [BORDER_STYLES[rule][BORDERS.RIGHT]]: _value,
          [BORDER_STYLES[rule][BORDERS.BOTTOM]]: _value,
          [BORDER_STYLES[rule][BORDERS.LEFT]]: _value,
        });
      });
    });
    
    this.changeNeighborsCellsStyle(rows.flatMap(row => row.map(cell => cell.id)), { rule, value, measure });
  }

  setOneBorder(rows, { rule, value, measure, applyToBorder }) {
    const tableRows = this.getRows();
    const _value = value + measure;
    
    rows.forEach(row => {
      row.forEach(cell => {
        cell.styles.setRules({
          [BORDER_STYLES[rule][applyToBorder]]: _value,
        });

        const rowIndex = tableRows.findIndex(row => row.find(_cell => _cell.id === cell.id));
        const cellIndex = tableRows[rowIndex].findIndex(_cell => _cell.id === cell.id);
        
        switch (true) {
          case applyToBorder === BORDERS.LEFT:
            if (tableRows[rowIndex][cellIndex - 1]) {
              tableRows[rowIndex][cellIndex - 1].styles.setRules({
                [BORDER_STYLES[rule][BORDERS.RIGHT]]: _value,
              })
            }
            break;
          case applyToBorder === BORDERS.RIGHT:
            if (tableRows[rowIndex][cellIndex + 1]) {
              tableRows[rowIndex][cellIndex + 1].styles.setRules({
                [BORDER_STYLES[rule][BORDERS.LEFT]]: _value,
              })
            }
            break;
          case applyToBorder === BORDERS.TOP:
            if (
              tableRows[rowIndex - 1] 
              && tableRows[rowIndex - 1].length === tableRows[rowIndex].length
              && tableRows[rowIndex - 1][cellIndex]
            ) {
              tableRows[rowIndex - 1][cellIndex].styles.setRules({
                [BORDER_STYLES[rule][BORDERS.BOTTOM]]: _value,
              })
            }
            break;
          case applyToBorder === BORDERS.BOTTOM:
            if (
              tableRows[rowIndex + 1] 
              && tableRows[rowIndex + 1].length === tableRows[rowIndex].length
              && tableRows[rowIndex + 1][cellIndex]
            ) {
              tableRows[rowIndex + 1][cellIndex].styles.setRules({
                [BORDER_STYLES[rule][BORDERS.TOP]]: _value,
              })
            }
            break;
          default:
            if (tableRows[rowIndex + 1][cellIndex]) {
              tableRows[rowIndex + 1][cellIndex].styles.setRules({
                [BORDER_STYLES[rule][BORDERS.TOP]]: _value,
              })
            }
            break;
        }
      });
    });
  }
  
  changeNeighborsCellsStyle(selectedCells, { rule, value, measure }) {
    const rows = this.getRows();
    const _value = value + measure;
    
    selectedCells.forEach(cell => {
      let rowIndex = null;
      let cellIndex = null;

      rows.forEach((_row, i) => {
        const index = _row.findIndex(_cell => _cell.id === cell);

        if (index >= 0) {
          rowIndex = i;
          cellIndex = index;
        }
      });
      
      // Left neighbor cell
      if (cellIndex > 0 && rows[rowIndex][cellIndex - 1] && !selectedCells.includes(rows[rowIndex][cellIndex - 1].id)) {
        rows[rowIndex][cellIndex - 1].styles.setRules({
          [BORDER_STYLES[rule][BORDERS.RIGHT]]: _value
        });
      }

      // Right neighbor cell
      if (
        cellIndex < rows[rowIndex].length - 1 
        && rows[rowIndex][cellIndex + 1] 
        && !selectedCells.includes(rows[rowIndex][cellIndex + 1].id)
      ) {
        rows[rowIndex][cellIndex + 1].styles.setRules({
          [BORDER_STYLES[rule][BORDERS.LEFT]]: _value
        });
      }

      // Top neighbor cell
      if (rowIndex > 0 && rows[rowIndex - 1][cellIndex] && !selectedCells.includes(rows[rowIndex - 1][cellIndex].id)) {
        rows[rowIndex - 1][cellIndex].styles.setRules({
          [BORDER_STYLES[rule][BORDERS.BOTTOM]]: _value
        });
      }

      // Bottom neighbor cell
      if (
        rowIndex < rows.length - 1 
        && rows[rowIndex + 1][cellIndex] 
        && !selectedCells.includes(rows[rowIndex + 1][cellIndex].id)
      ) {
        rows[rowIndex + 1][cellIndex].styles.setRules({
          [BORDER_STYLES[rule][BORDERS.TOP]]: _value
        });
      }
    });
  }
  
  changeCellValue(selectedCell) {
    const cell = this.rows
      .reduce((acc, row) => [...acc, ...row.cells], [...this.staticHeaders, ...this.staticFooters, ...this.headers])
      .find(_cell => _cell.id === selectedCell.id);

    const staticGroup = this.getStaticRowGroup(cell.id);

    if (staticGroup) {
      this.setStaticCellColspan(cell.id, selectedCell.colspan);
    } else {
      this.updateRowBasedOnColspan(this.getRow(cell), cell, selectedCell.colspan);
    }
    //check rowspan only for rows
    const isHeader = this.headers.some(c => c.id === cell.id) ||
      (this.staticHeaders && this.staticHeaders.some(c => c.id === cell.id)) ||
      (this.staticFooters && this.staticFooters.some(c => c.id === cell.id));
    if (!isHeader) {
      this.updateTableBasedOnRowspan(cell, selectedCell.rowspan);
    }
    
    cell.setValue(selectedCell.valueHtml);
    cell.setLink(selectedCell.link)
    // A caller that does not carry the name (the blur commit of a cell being edited) must not erase it.
    if (selectedCell.mappingName !== undefined) cell.setMappingName(selectedCell.mappingName);
    cell.setSrc('');
  }
  
  updateRowBasedOnColspan(row, cell, _colspan) {
    if (_colspan < 1) return;
    
    const cellColspan = Number(cell.colspan);
    let colspan = Number(_colspan);
    const cellIndex = row.findIndex(_cell => _cell.id === cell.id);
    
    let availableColspanForCell = cellColspan;
    
    for (let i = cellIndex + 1; i < row.length; i++) {
      if (row[i].colspan > 1) break;
      if (row[i].colspan === 1) availableColspanForCell++;
    }
    
    if (cellColspan < colspan) {
      if (colspan > availableColspanForCell) {
        colspan = availableColspanForCell;
      }

      const diff = colspan - cellColspan;

      row.splice(cellIndex + 1, diff);
      
    } else if (colspan < cellColspan) {
      const diff = cellColspan - colspan;

      for (let i = 0; i < diff; i++) {
        const styles = cell.styles.getStyles();
        const newCell = new TableBuilder.TableCell();
        newCell.styles.setRules(styles);

        row.splice(cellIndex + 1, 0, newCell);
      }
    }

    cell.setColspan(colspan);
  }

  updateTableBasedOnRowspan(cell, newRowspan) {
    if (newRowspan < 1) return;

    const currentRowspan = Number(cell.rowspan ?? 1);
    const rowspan = Number(newRowspan);

    const rowIndex = this.rows.findIndex(row => row.cells.includes(cell));
    if (rowIndex < 0) return;
    const colIndex = this.rows[rowIndex].cells.findIndex(c => c.id === cell.id);

    if (rowspan > currentRowspan) {
      for (let r = 1; r < rowspan; r++) {
        const targetRow = this.rows[rowIndex + r];
        if (!targetRow) continue;

        const targetCell = targetRow.cells[colIndex];
        if (targetCell) {
          targetRow.cells.splice(colIndex, 1);
        }
      }
    }

    if (rowspan < currentRowspan) {
      const diff = currentRowspan - rowspan;

      for (let r = 1; r <= diff; r++) {
        const targetRow = this.rows[rowIndex + r];
        if (!targetRow) continue;

        const styles = cell.styles.getStyles();
        const newCell = new TableBuilder.TableCell();
        newCell.styles.setRules(styles);

        targetRow.cells.splice(colIndex, 0, newCell);
      }
    }

    cell.setRowspan(rowspan);
  }

  /**
   * One static row: its cells are ordinary cells of this table's grid, spanning the columns they
   * were given, so they line up with the columns below. A row of one cell is the full-width `<td>`
   * every static row has always been.
   *
   * @param {TableCell[]} group
   */
  async staticRowToHTML(group, tableColumnMode) {
    const cells = await Promise.all(group.map(cell => cell.toHTML(tableColumnMode, this.truncateLongText)));

    return `<tr ${this.getHeaderStyleAttribute()}>${cells.join('')}</tr>`;
  }

  /**
   * Lays the rendered body rows out with the static rows in their places — around the table, or
   * straight after the body row they are anchored to.
   */
  async tbodyToHTML(bodyRowsHtml, headerRowHtml, tableColumnMode, bodyCellRows) {
    const parts = [];

    for (const row of this.getVisualRows(bodyCellRows)) {
      if (row.kind === 'static') {
        parts.push(await this.staticRowToHTML(row.cells, tableColumnMode));
      } else if (row.kind === 'header') {
        parts.push(headerRowHtml);
      } else {
        parts.push(bodyRowsHtml[row.index]);
      }
    }

    return parts.join('');
  }

  async toHTML() {
    this.syncStaticRowsColspan();
    this.applyBorderBoxWidths();
    const tableColumnMode = this.getTableColumnMode();

    if (tableColumnMode) {
      const rows = this.getTransposedTableRows();
      const bodyRows = await Promise.all(rows.map(/** @param {TableRow} row */ async row => await row.toHTML(tableColumnMode, this.truncateLongText)));
      // The headers are the transposed rows' first column here, so there is no header row of its own.
      const tbody = await this.tbodyToHTML(bodyRows, '', tableColumnMode, rows.map(row => row.cells));

      return DOMPurify.sanitize(`<table ${this.getStyleAttribute(tableColumnMode)}><tbody>${tbody}</tbody></table>`);
    } else {
      const occupied = {}; // row-col => true
      const bodyRows = [];

      let headerRowHTML = '';
      if (this.headers && this.headers.length > 0 && this.headerVisible !== false) {
        const headerCells = await Promise.all(this.headers.map(/** @param {TableCell} cell */ cell => cell.toHTML(tableColumnMode, this.truncateLongText)));
        headerRowHTML = `<tr ${this.getHeaderStyleAttribute()}>${headerCells.join('')}</tr>`;
      }

      for (let rowIndex = 0; rowIndex < this.rows.length; rowIndex++) {
        const row = this.rows[rowIndex];
        let htmlCells = [];
        let colIndex = 0;
        /** @var {TableCell} cell */
        for (const cell of row.cells) {
          while (occupied[`${rowIndex}-${colIndex}`]) {
            colIndex++;
          }

          const rowspan = cell.rowspan ?? 1;
          const colspan = cell.colspan ?? 1;

          for (let r = 1; r < rowspan; r++) {
            for (let c = 0; c < colspan; c++) {
              occupied[`${rowIndex + r}-${colIndex + c}`] = true;
            }
          }

          const cellHTML = await cell.toHTML(this.getTableColumnMode(), this.truncateLongText);
          htmlCells.push(cellHTML);
          colIndex += colspan;
        }

        bodyRows.push(`<tr ${row.getStyleAttribute(this.getTableColumnMode())}>${htmlCells.join('')}</tr>`);
      }

      const tbody = await this.tbodyToHTML(bodyRows, headerRowHTML, tableColumnMode);

      return DOMPurify.sanitize(`<table ${this.getStyleAttribute(tableColumnMode)}><tbody>${tbody}</tbody></table>`);
    }
  }

  async getSVGSize() {
    const fonts = await this.getTableFonts();
    let table = await this.toHTML();

    table = table.replace(/&nbsp;/g, '&#160;');
    table = table.replace(/<br\s*\/?>/gi, '<br/>');
    // an <img> inlined from cell HTML has no closing slash either
    table = table.replace(/<img\b([^>]*?)\/?>/gi, '<img$1/>');

    const div = `<div xmlns="http://www.w3.org/1999/xhtml">${fonts}${table}</div>`;

    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg">
            <foreignObject width="100%" height="100%">
                ${div}
            </foreignObject>
        </svg>
    `;

    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.visibility = 'hidden';
    container.style.left = '-99999px';

    document.body.appendChild(container);

    container.innerHTML = svg;

    const tableElement = container.querySelector('table');

    if (document.fonts?.ready) {
      await document.fonts.ready;
    }

    const rect = tableElement.getBoundingClientRect();

    document.body.removeChild(container);

    return {
        width: Math.ceil(rect.width),
        height: Math.ceil(rect.height)
    };
  }

  /**
   * @param {{size: object|null, hairlineScale: number}} options
   *   `size` reuses a measurement instead of paying for the iframe again — the caller already has
   *   one whenever it is only re-rasterising. `hairlineScale` is the scale the raster will be
   *   *displayed* at; below 1 the borders are painted thicker so they survive it. Both default to
   *   the old behaviour, so every existing caller is unchanged.
   */
  async toSVG({ size = null, hairlineScale = 1 } = {}) {
    const fonts = await this.getTableFonts();
    let tableColumnMode = this.tableColumnMode;
    
    //fix for old tables as seen on resources/js/frontend/components/modules/layouts-page/modals/SimpleTableBuilderModal.vue
    if (!this.headers.length && tableColumnMode === false) {
      tableColumnMode = true;
    }

    size = size || await this.getOuterDimension();

    let table = boostHairlines(await this.toHTML(), hairlineScale, this.getHairlineEdges());

    // replace non breaking spaces with a numeric entity XML-safe equivalent 
    table = table.replace(/&nbsp;/g, '&#160;');
    // ensure <br> is XML-safe <br/>
    table = table.replace(/<br\s*\/?>/gi, '<br/>');
    // an <img> inlined from cell HTML has no closing slash either
    table = table.replace(/<img\b([^>]*?)\/?>/gi, '<img$1/>');

    const div = `<div xmlns="http://www.w3.org/1999/xhtml">${fonts}${BOLD_SYNTHESIS_STYLE}${table}</div>`;
    const foreignObject = `<foreignObject width="100%" height="100%">${div}</foreignObject>`;

    const svg =  `<svg xmlns="http://www.w3.org/2000/svg" width="${size.width + 2}" height="${size.height + 2}">${foreignObject}</svg>`;

    return svg;

  }

  /**
   * @param {TableCell} cell
   */
  staticCellToData(cell, tableColumnMode) {
    return {
      ...cell.toData(tableColumnMode),
      position: cell.position || 'top',
      staticRowIndex: cell.staticRowIndex,
      afterRowIndex: cell.afterRowIndex,
    };
  }

  toData(tableColumnMode = null) {
    this.applyBorderBoxWidths();

    tableColumnMode = tableColumnMode === null
      ? this.getTableColumnMode()
      : tableColumnMode;

    let headers = tableColumnMode
      ? []
      : this.headers.map(/** @param {TableColumn} column */ column => column.toData(tableColumnMode));
    let rows = tableColumnMode && this.headers.length
      ? this.getTransposedTableRows()
      : this.rows;
    // let rows = this.rows;
    return {
      id: this.id,
      stringStyles: this.styles.getStyle(tableColumnMode),
      styles: this.styles,
      headers: headers,
      staticHeaders: this.staticHeaders.map(cell => this.staticCellToData(cell, tableColumnMode)),
      staticFooters: this.staticFooters.map(cell => this.staticCellToData(cell, tableColumnMode)),
      staticRows: this.getStaticRows().map(cell => this.staticCellToData(cell, tableColumnMode)),
      rows: rows.map(/** @param {TableRow} row */ row => row.toData(tableColumnMode)),
      headerRowStyles: this.headerRowStyles,
      autoFontSize: this.autoFontSize,
      autoFontSizeValue: this.autoFontSizeValue,
      tableColumnMode: this.getTableColumnMode(),
      headerVisible: this.headerVisible,
      mergeSameValues: this.mergeSameValues,
      truncateLongText: this.truncateLongText,
      borderBoxWidths: this.borderBoxWidths,
      defaultCellPaddingShorthand: this.defaultCellPaddingShorthand,
    }
  }

  async toBlob(options) {
    const data = await this.toSVG(options);

    return new Blob([data], {
      type: "image/svg+xml;charset=utf-8"
    });
  }

  async getDimension() {
    const $table = $(await this.toHTML());

    // Create an empty iframe so the css styles from it'sRapid don't affect the metrics of the table
    let $iframe = $('<iframe>', { style: 'position:absolute; visibility:hidden; width:0; height:0; border:none;' });
    const fonts = await this.getTableFonts(); // Contains base64-encoded fonts in <style>

    $iframe.appendTo('body');

    let iframeDoc = $iframe[0].contentDocument || $iframe[0].contentWindow.document;
    
    // Append a new blank <html> and <body> to the iframe
    iframeDoc.open();
    iframeDoc.write('<!DOCTYPE html><html><head></head><body></body></html>');
    iframeDoc.close();

    let $iframeHead = $(iframeDoc.head);
    let $iframeBody = $(iframeDoc.body);

    // Inject font styles into the iframe's head
    $iframeHead.append(fonts);

    // Append the table to the iframe's body
    $iframeBody.append($table);

    // Ensure images load
    await this.waitForImagesToLoad($table);

    // Wait for fonts to be fully applied
    await new Promise((resolve) => setTimeout(resolve, 100)); // Small delay to allow styles to apply

    // Alternative: Ensure all fonts are loaded before measuring
    if (iframeDoc.fonts && iframeDoc.fonts.ready) {
        await iframeDoc.fonts.ready;
    }

    // Reflow trick: Force browser to apply styles before measuring
    $iframeBody[0].offsetHeight;

    // Measure table dimensions inside the iframe
    let width = Math.ceil($table.width());
    let height = Math.ceil($table.height());
    const columns = this.measureColumns($table, $iframe[0].contentWindow);

    // Clean up
    $iframe.remove();

    return { width, height, ...columns };
  }

  waitForImagesToLoad(table) {
    return new Promise(resolve => {
      const images = table.find('img').toArray();
      let loaded = 0;

      const handleImageLoading = () => {
        loaded++;
        if (images.length === loaded) resolve();
      }
      
      if (images.length === 0) {
        resolve();
      }
      
      images.forEach(img => {
        if (img.complete) {
          handleImageLoading();
        } else {
          $(img).on("load", () => {
            handleImageLoading();
          });

          $(img).on("error", () => {
            console.log('An error occurred while loading the table image.');
            
            handleImageLoading();
          });
        }
      })
    });
  }

  async getOuterDimension() {
    const $table = $(await this.toHTML());

    // Create an empty iframe so the css styles from it'sRapid don't affect the metrics of the table
    const urlParams = new URLSearchParams(window.location.search);
    const isDebug = urlParams.get('debug') === 'true';
    let $iframe = isDebug ? $('<iframe>', { style: 'position:absolute;border:none;z-index: 9999;width: 100%;height: 100%;' }) : $('<iframe>', { style: 'position:absolute; visibility:hidden; width:0; height:0; border:none;top: 1000px;' });
    const fonts = await this.getTableFonts(); // Contains base64-encoded fonts in <style>

    $iframe.appendTo('body');

    let iframeDoc = $iframe[0].contentDocument || $iframe[0].contentWindow.document;
    
    // Append a new blank <html> and <body> to the iframe
    iframeDoc.open();
    iframeDoc.write('<!DOCTYPE html><html><head></head><body></body></html>');
    iframeDoc.close();

    let $iframeHead = $(iframeDoc.head);
    let $iframeBody = $(iframeDoc.body);

    // Inject font styles into the iframe's head
    $iframeHead.append(fonts);

    // Append the table to the iframe's body
    $iframeBody.append($table);

    // Ensure images load
    await this.waitForImagesToLoad($table);

    // Wait for fonts to be fully applied
    await new Promise((resolve) => setTimeout(resolve, 100)); // Small delay to allow styles to apply

    // Alternative: Ensure all fonts are loaded before measuring
    if (iframeDoc.fonts && iframeDoc.fonts.ready) {
        await iframeDoc.fonts.ready;
    }

    // Reflow trick: Force browser to apply styles before measuring
    $iframeBody[0].offsetHeight;

    // Measure table dimensions inside the iframe. Rounded up, and up everywhere else too: the same
    // fractional measurement rounded three different ways is what had the builder, the canvas and
    // this readout each reporting a different width for one table.
    let width = Math.ceil($table.outerWidth());
    let height = Math.ceil($table.outerHeight());
    const columns = this.measureColumns($table, $iframe[0].contentWindow);

    // Clean up
    if (!isDebug) {
      $iframe.remove();
    }

    return { width, height, ...columns };
  }

  /**
   * What the columns actually came out as, measured on the rendered table:
   *
   * - `columnsWidth` — the widths added up, to compare against the ones that were set. A Fixed
   *   Width is only a minimum, so this is what the table really holds.
   * - `columnOverflows` — the columns whose content will not fit the width they were given. That
   *   is what makes two tables with the same column widths stop lining up, so it is worth
   *   surfacing.
   * - `columnsSurplus` / `naturalColumnsWidth` — how much of the rendered width is the table being
   *   pinned wider than its columns need, which is a different problem with a different fix.
   * - `tableOuterWidth` — the width the table itself came out at, so the column total and the W
   *   field can be reconciled instead of just disagreeing. What stands between them is the cells'
   *   paddings and borders, or with border-box widths only the table's own border.
   *
   * The requested width is read from the rendered `<td>`'s own inline style, so it is compared
   * against exactly what was measured. Cells spanning columns are skipped — their width is not
   * one column's — and the grid is walked with the same occupancy map `toHTML()` uses, so
   * rowspans do not shift the column a cell is credited to.
   */
  measureColumns($table, frameWindow) {
    const occupied = {};
    const columns = new Map();

    $table.find('tr').each((rowIndex, tr) => {
      let columnIndex = 0;

      Array.from(tr.cells).forEach(td => {
        while (occupied[`${rowIndex}-${columnIndex}`]) columnIndex++;

        const colspan = td.colSpan || 1;
        const rowspan = td.rowSpan || 1;

        for (let r = 1; r < rowspan; r++) {
          for (let c = 0; c < colspan; c++) {
            occupied[`${rowIndex + r}-${columnIndex + c}`] = true;
          }
        }

        if (colspan === 1 && !columns.has(columnIndex)) {
          columns.set(columnIndex, {
            td,
            requested: parseFloat((td.getAttribute('style') || '').match(/(?:^|;)\s*width:\s*([\d.]+)px/)?.[1]) || 0,
            used: Math.round(parseFloat(frameWindow.getComputedStyle(td).width)) || 0,
          });
        }

        columnIndex += colspan;
      });
    });

    const measured = [...columns.values()];
    const columnsWidth = measured.reduce((sum, column) => sum + column.used, 0);
    // Read before the natural pass moves anything, on the same basis and rounding the dimension
    // methods use, so this line adds up to the W they report.
    const tableOuterWidth = Math.ceil($table.outerWidth());
    const natural = this.measureNaturalColumns($table, frameWindow, measured);
    const naturalColumnsWidth = natural.columns.reduce((sum, width) => sum + width, 0);
    const listed = new Set();

    return {
      columnsWidth,
      naturalColumnsWidth,
      tableOuterWidth,
      naturalTableWidth: natural.outerWidth,
      // A table pinned wider than its columns hands the slack to every one of them. That is not a
      // column running out of room, and reporting it as one sends people chasing a moving number.
      columnsSurplus: Math.max(0, columnsWidth - naturalColumnsWidth),
      // Several columns can be set to the same width and grow to the same place; listing that
      // pair once says everything the second one would.
      columnOverflows: measured
        .map((column, index) => ({ requested: column.requested, used: natural.columns[index] }))
        .filter(({ requested, used }) => {
          if (!requested || used <= requested + 1 || listed.has(`${requested}-${used}`)) return false;

          listed.add(`${requested}-${used}`);

          return true;
        }),
    };
  }

  /**
   * Content pressure can only be read with the table's own width off, since that width stretches
   * every column to fill it. The measuring iframe is 0 wide, so the table also needs room to
   * expand into — without it an unpinned table collapses to its minimum and every column lies.
   */
  measureNaturalColumns($table, frameWindow, measured) {
    const table = $table[0];
    const body = table.ownerDocument.body;
    const tableWidth = table.style.width;
    const tableMinWidth = table.style.minWidth;
    const bodyWidth = body.style.width;

    table.style.width = '';
    table.style.minWidth = '';
    body.style.width = '100000px';

    const columns = measured.map(({ td }) => Math.round(parseFloat(frameWindow.getComputedStyle(td).width)) || 0);
    const outerWidth = Math.ceil($table.outerWidth());

    table.style.width = tableWidth;
    table.style.minWidth = tableMinWidth;
    body.style.width = bodyWidth;

    return { columns, outerWidth };
  }

  getRow(cell) {
    const staticGroup = this.getStaticRowGroup(cell.id);

    if (staticGroup) {
      return staticGroup;
    }

    if (this.headers.find(_cell => _cell.id === cell.id)) {
      return this.headers;
    }

    return this.rows.find(row => {
      return row.cells.find(_cell => _cell.id === cell.id);
    }).cells;
  }

  /**
   * Every row of the table in the order it renders, tagged with what it is. A static row sits in
   * the band above or below the table unless it was anchored to a body row, in which case it
   * renders straight after that row (-1 = straight under the header row). One list, so the HTML,
   * the row removal and the builder preview can never disagree about the order.
   *
   * @param {TableCell[][]|null} bodyRows - the rendered body rows, when they are not the stored
   *   ones (Column Mode transposes them).
   * @return {{kind: 'static'|'header'|'body', cells: TableCell[], index: number|null}[]}
   */
  getVisualRows(bodyRows = null) {
    const body = bodyRows || this.rows.map(row => row.cells);
    const bands = { top: [], bottom: [] };
    const anchored = new Map();

    this.getStaticRowGroups().forEach(group => {
      const anchor = group[0].afterRowIndex;

      if (!Number.isFinite(anchor)) {
        bands[(group[0].position || 'top') === 'bottom' ? 'bottom' : 'top'].push(group);
        return;
      }

      // A row the anchor pointed at can be gone by now; it keeps the nearest place instead.
      const key = Math.min(Math.max(anchor, -1), body.length - 1);

      anchored.set(key, [...(anchored.get(key) || []), group]);
    });

    const asStatic = group => ({ kind: 'static', cells: group, index: null });
    const rows = bands.top.map(asStatic);

    if (this.headers.length) {
      rows.push({ kind: 'header', cells: this.headers, index: null });
    }

    (anchored.get(-1) || []).forEach(group => rows.push(asStatic(group)));

    body.forEach((cells, index) => {
      rows.push({ kind: 'body', cells, index });
      (anchored.get(index) || []).forEach(group => rows.push(asStatic(group)));
    });

    return [...rows, ...bands.bottom.map(asStatic)];
  }

  getRows() {
    return this.getVisualRows().map(row => row.cells);
  }
  
  /**
   * The rows the on-screen columns are worked out from. Static rows are in it: their cells sit in
   * the same grid, so a static cell covering one column shares that column's width with the
   * header and body cells under it — that is what lines a static row up with the table.
   *
   * Column Mode is the exception: there the stored rows are transposed on render but the static
   * rows are not, so a static cell's stored position says nothing about the column it appears in.
   */
  getGridRows() {
    const staticRows = this.getTableColumnMode()
      ? { top: [], bottom: [] }
      : { top: this.getStaticRowGroups('top'), bottom: this.getStaticRowGroups('bottom') };

    return [
      ...staticRows.top,
      this.headers,
      ...this.rows.map(row => row.cells),
      ...staticRows.bottom,
    ].filter(row => Array.isArray(row));
  }

  /**
   * On-screen column each cell starts in. A cell's index inside its row is not its
   * column as soon as spans exist: a colspan pushes the following cells right, and a
   * rowspan from an earlier row takes a slot that is missing from this row's array.
   *
   * @return {Map<string, number>}
   */
  getColumnByCellId() {
    const occupied = {};
    const columnByCellId = new Map();

    this.getGridRows().forEach((row, rowIndex) => {
      let colIndex = 0;

      row.forEach(cell => {
        while (occupied[`${rowIndex}-${colIndex}`]) colIndex++;

        columnByCellId.set(cell.id, colIndex);

        const colspan = cell.colspan || 1;
        const rowspan = cell.rowspan || 1;

        for (let r = 1; r < rowspan; r++) {
          for (let c = 0; c < colspan; c++) {
            occupied[`${rowIndex + r}-${colIndex + c}`] = true;
          }
        }

        colIndex += colspan;
      });
    });

    return columnByCellId;
  }

  // Cells sharing an on-screen column with the selected ones. Cells spanning several
  // columns are left out: their width is not a single column's width, so a selected
  // spanning cell only ever gets the value on itself.
  getCellsByColumns(selectedCells) {
    const columnByCellId = this.getColumnByCellId();
    const cells = this.getGridRows().reduce((acc, row) => [...acc, ...row], []);

    // A static cell sizes itself: it neither seeds its column nor takes a width from it. Only the
    // selected ones come along, so a static row and the body under it are set independently.
    const staticIds = new Set(this.getStaticRows().map(cell => cell.id));

    const columns = new Set();

    cells.forEach(cell => {
      if (selectedCells.includes(cell.id) && !staticIds.has(cell.id) && (cell.colspan || 1) === 1) {
        columns.add(columnByCellId.get(cell.id));
      }
    });

    const byColumn = cells.filter(cell => selectedCells.includes(cell.id)
      || (!staticIds.has(cell.id) && (cell.colspan || 1) === 1 && columns.has(columnByCellId.get(cell.id))));

    // In Column Mode the static rows are out of the grid above, so a selected static cell would
    // lose the rule entirely; it still takes it on itself.
    const missedStaticCells = this.getStaticRows()
      .filter(cell => selectedCells.includes(cell.id) && !byColumn.includes(cell));

    return [...byColumn, ...missedStaticCells];
  }
  
  async getTableFonts() {
    const fonts = [this.styles.getRule('font-family')];
    
    const cells = this.rows.reduce((acc, row) => ([...acc, ...row.cells]), [...this.headers, ...this.staticHeaders, ...this.staticFooters]);
    
    cells.forEach(cell => {
      const fontFamily = cell.styles.getRule('font-family');
      
      if (cell.valueHtml) {
        let font = extractInlineFont(cell.valueHtml);
        
        if (font) {
          fonts.push(font);
        }
      }
      
      if (!fonts.includes(fontFamily) && fontFamily) {
        fonts.push(fontFamily);
      }
    });
    
    return `<style>${await getFontFaceStyle(fonts)}</style>`;
  }

  getHeaderStyleAttribute() {
    const styles = this.headerRowStyles.getStyle(this.getTableColumnMode());

    return styles ? `style="${styles}"` : '';
  }

  getSumOfColumnWidths() {
    const widthOf = (cell) => {
      if (!cell) return 0;

      const width = cell.styles.getRule('width')
        ? parseInt(cell.styles.getRule('width')) || 0
        : 0;

      const minWidth = cell.styles.getRule('min-width')
        ? parseInt(cell.styles.getRule('min-width')) || 0
        : 0;

      return Math.max(width, minWidth);
    };

    // In Column Mode the table is transposed, so the on-screen columns are the
    // header column plus one column per body row. Sum a representative cell from
    // each on-screen column so the total reflects what the user sees.
    if (this.getTableColumnMode()) {
      let sum = this.headers[0] && this.headerVisible !== false ? widthOf(this.headers[0]) : 0;

      this.rows.forEach(row => {
        sum += widthOf(row.cells[0]);
      });

      return sum;
    }

    // Widths belong to columns, not header cells: a merged header cell spans several columns but
    // carries at most one column's width, so summing the header row alone under-reports every
    // column hidden behind it (a 4-column table read as 1271 instead of 2079). The header stays
    // the first source so classic one-cell-per-column tables keep their exact numbers; only the
    // columns a merged cell covers fall through to the first single-column cell below them.
    const columnWidths = {};
    const occupied = {};

    this.getVisualRows().forEach((row, rowIndex) => {
      let columnIndex = 0;

      row.cells.forEach(cell => {
        while (occupied[`${rowIndex}-${columnIndex}`]) columnIndex++;

        const colspan = cell.colspan || 1;
        const rowspan = cell.rowspan || 1;

        for (let r = 1; r < rowspan; r++) {
          for (let c = 0; c < colspan; c++) occupied[`${rowIndex + r}-${columnIndex + c}`] = true;
        }

        if (colspan === 1 && !(columnIndex in columnWidths)) {
          const width = widthOf(cell);

          if (width) columnWidths[columnIndex] = width;
        }

        columnIndex += colspan;
      });
    });

    return Object.values(columnWidths).reduce((acc, width) => acc + width, 0);
  }

  getColumnStyles(selectedCell) {
    return this.getCellsByColumns([selectedCell.id]).map(cell => cell.getStyles());
  }

  applyColumnStylesToColumn(selectedCell, columnStyles) {
    const cellsByColumn = this.getCellsByColumns([selectedCell.id]);
    
    cellsByColumn.forEach((cell, i) => {
      cell.styles.setRules(columnStyles[i]);
    });
  }

  getTransposedTableRows() {
    let newRows = [];
    for (let i = 0; i < this.headers.length; i++) {
      /** @var {TableRow} row */
      let row = new TableBuilder.TableRow();

      /** @var {TableCell} header */
      let header = this.headers[i];
      header.isHeader = true;

      // Use a deterministic id derived from the column so the row keeps a stable
      // identity across re-renders. Without this every toData() produces a fresh
      // uuid, which breaks Vue's :key and recreates the DOM (losing cell focus).
      row.setId(`transposed-${header.id}`);

      if (this.headerVisible !== false) {
        row.cells.push(header);
      }

      for (let j = 0; j < this.rows.length; j++) {
        let cell = this.rows[j].cells[i];
        row.cells.push(cell);
      }

      newRows.push(row);
    }

    return newRows;
  }

  extractStyleWidthNumber(html) {
    const match = html.match(/style=["'][^"']*width\s*:\s*(\d+)/i);
    return match ? Number(match[1]) : null;
  }

  extractStyleHeightNumber(html) {
    const match = html.match(/style=["'][^"']*height\s*:\s*(\d+)/i);
    return match ? Number(match[1]) : null;
  }

  getFixedWidth(){
    return this.extractStyleWidthNumber(this.getStyleAttribute(this.getTableColumnMode()));
  }

  getFixedHeight(){
    return this.extractStyleHeightNumber(this.getStyleAttribute(true));
  }

}
